import { normalizePathForTracking } from './slug-normalization'
import { logger } from '@/lib/logger'

export interface TimeOnTaskInstance {
  trackSession(taskName: string): Promise<void> | void
}

const DEFAULT_SYNC_URL = '/api/totter'
const WRITING_LEGENDS_PRODUCT = 'writing_legends'

type TimeOnTaskConstructor = new (
  studentId: string,
  options: {
    product: string
    syncOptions: {
      syncUrl: string
    }
  },
) => TimeOnTaskInstance

type CreateTrackerOptions = {
  studentId: number | string
  syncUrl?: string
  TimeOnTaskClass?: TimeOnTaskConstructor
}

type TrackPathOptions = CreateTrackerOptions & {
  path: string | null
}

type TrackerState = {
  studentId: string
  syncUrl: string
  timeOnTask: TimeOnTaskInstance
}

let trackerState: TrackerState | undefined

export function taskNameForPath(path: string | null) {
  if (!path) return null

  const trimmedPath = path.trim()
  if (trimmedPath.length === 0) return null

  try {
    const pathname = new URL(trimmedPath, 'http://localhost').pathname
    return normalizePathForTracking(pathname)
  } catch (error) {
    logger.warn(
      { path: trimmedPath, error },
      'Failed to parse path for time-on-task tracking',
    )
    return null
  }
}

async function resolveTimeOnTaskClass(TimeOnTaskClass?: TimeOnTaskConstructor) {
  if (TimeOnTaskClass) return TimeOnTaskClass

  const { TimeOnTask } = await import('@blakeelearning/totter')
  return TimeOnTask as TimeOnTaskConstructor
}

export async function getOrCreateTimeOnTaskTracker({
  studentId,
  syncUrl = DEFAULT_SYNC_URL,
  TimeOnTaskClass,
}: CreateTrackerOptions) {
  const normalisedStudentId = String(studentId)

  if (
    trackerState?.studentId === normalisedStudentId &&
    trackerState.syncUrl === syncUrl
  ) {
    return trackerState.timeOnTask
  }

  const ResolvedTimeOnTaskClass = await resolveTimeOnTaskClass(TimeOnTaskClass)

  const timeOnTask = new ResolvedTimeOnTaskClass(normalisedStudentId, {
    product: WRITING_LEGENDS_PRODUCT,
    syncOptions: {
      syncUrl,
    },
  })

  trackerState = {
    studentId: normalisedStudentId,
    syncUrl,
    timeOnTask,
  }

  return timeOnTask
}

export async function trackTimeOnTaskPath({
  path,
  studentId,
  syncUrl,
  TimeOnTaskClass,
}: TrackPathOptions) {
  const taskName = taskNameForPath(path)
  if (!taskName) return

  const trackerOptions: CreateTrackerOptions = {
    studentId,
  }

  if (syncUrl) {
    trackerOptions.syncUrl = syncUrl
  }

  if (TimeOnTaskClass) {
    trackerOptions.TimeOnTaskClass = TimeOnTaskClass
  }

  const timeOnTask = await getOrCreateTimeOnTaskTracker(trackerOptions)

  await timeOnTask.trackSession(taskName)
}

export function resetTimeOnTaskTrackerForTests() {
  trackerState = undefined
}
